/**
 * Local persistence of an in-progress ranking, so a reload or an app switch
 * mid-turn does not throw away the order the player has been arranging
 * (DESIGN §4, "reload restores the same 12-person sequence").
 *
 * ## Why this is local and not server state
 *
 * There is no draft ranking anywhere in the wire protocol: `submit_ranking` is
 * the only ranking message and it carries a complete, final ordering. Nothing
 * partial is ever sent, by design — the server must not hold a half-formed
 * answer it could accidentally score. So the authoritative snapshot genuinely
 * cannot restore this, and the restoration `RankingInteractionProps` promises
 * has to come from the device.
 *
 * ## Why `sessionStorage`
 *
 * SPEC §6's posture is that no durable personal data exists anywhere. A draft
 * ranking is the player's own unsent working state; `sessionStorage` dies with
 * the tab, never leaves the device, and never reaches D1 or any URL. It is also
 * scoped per turn, so it can never leak into another turn's board, and every
 * draft for a room is dropped on "play again".
 */
import type { PlayerId } from '@idgame/protocol';

const PREFIX = 'idgame.rankdraft';

function keyFor(roomCode: string, turnIndex: number): string {
  return `${PREFIX}.${roomCode}.${turnIndex}`;
}

function store(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Private mode, or storage disabled. Drafting is a nicety, never a
    // requirement — degrade to no persistence rather than breaking the turn.
    return null;
  }
}

export function loadRankingDraft(
  roomCode: string | null,
  turnIndex: number | null,
): PlayerId[] | undefined {
  if (roomCode === null || turnIndex === null) return undefined;
  try {
    const raw = store()?.getItem(keyFor(roomCode, turnIndex));
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return undefined;
    // Anything that is not a player id is dropped rather than preserved as a
    // hole: the board is always complete, and the model treats what survives as
    // a preference, so a mangled draft degrades to a partial restore.
    return parsed.filter((entry): entry is PlayerId => typeof entry === 'string');
  } catch {
    return undefined;
  }
}

export function saveRankingDraft(
  roomCode: string | null,
  turnIndex: number | null,
  ranking: readonly PlayerId[],
): void {
  if (roomCode === null || turnIndex === null) return;
  try {
    store()?.setItem(keyFor(roomCode, turnIndex), JSON.stringify(ranking));
  } catch {
    /* quota or private mode; the turn continues without a draft */
  }
}

/** Drop every draft for a room — used when the game resets (SPEC §2.6). */
export function clearAllRankingDrafts(roomCode: string | null): void {
  const storage = store();
  if (!storage || roomCode === null) return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(`${PREFIX}.${roomCode}.`)) doomed.push(key);
    }
    for (const key of doomed) storage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}
